/**
 * The ink of the number drawn on a weather-map station badge (#336 MR2b,
 * decision 4), shared by the webapp's DOM markers and the mobile MapLibre
 * symbol layer.
 *
 * Whichever of black or white contrasts more with the badge, measured the
 * WCAG way. The YIQ threshold the badges used before (`isLight`, through
 * `Units.getFontColorFromBackground`) picks white on mid-luminance hues and
 * left 18 of the 96 colour classes under 4.5:1 -- white on the -1 °C blue
 * `#00a3fc` at 2.75:1. The max-contrast pick clears 4.5:1 on all of them
 * (the lowest is 4.60:1, white on the -18 °C violet `#c100f3`);
 * `__tests__/utils/WeatherMap/stationMarkerInk.test.ts` sweeps every class.
 *
 * Imports the leaf module, never the utils barrel (`importCycles.test.ts`).
 */
import {contrastRatio, flatten} from '../Colors';

/**
 * What a translucent badge is composited onto before it is measured:
 * OpenFreeMap positron's land, light in both themes, under every mobile
 * badge (mobile `theme/index.tsx` `land`; the webapp's copy is `land` in
 * `theme/tokens.ts`). Only the trace-rain class is translucent. (The
 * webapp's rain drop is a filled drop inside a `background.paper` outline,
 * so a translucent drop is composited onto the map, the outline's paper rim
 * and shine stroke aside.)
 */
export const STATION_BADGE_BACKDROP = '#f2f3f0';

export type StationMarkerInk = '#000000' | '#ffffff';

const BLACK: StationMarkerInk = '#000000';
const WHITE: StationMarkerInk = '#ffffff';

/**
 * Black or white, whichever reads better on `color` (any CSS colour chroma
 * parses; alpha composited onto {@link STATION_BADGE_BACKDROP}). Black wins
 * a tie.
 */
export const stationMarkerInk = (color: string): StationMarkerInk => {
  const badge = flatten(color, STATION_BADGE_BACKDROP);
  return contrastRatio(BLACK, badge) >= contrastRatio(WHITE, badge)
    ? BLACK
    : WHITE;
};
